import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const ContactPopup = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('subject', formData.subject);
      form.append('message', formData.message);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        body: form,
      });

      const data = await response.json();

      if (data.success) {
        setSubmitMessage(t('contact.messageSent'));
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => {
          onClose();
          setSubmitMessage('');
        }, 2000);
      } else {
        setSubmitMessage(t('contact.messageError'));
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessage(t('contact.messageError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-kioo-primary text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">{t('contact.title')}</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-green-100 mt-2">{t('contact.getInTouch')}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.name')} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-kioo-primary"
                placeholder={t('contact.name')}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.email')} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-kioo-primary"
                placeholder={t('contact.email')}
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.subject')} *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-kioo-primary"
                placeholder={t('contact.subject')}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.message')} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-kioo-primary resize-none"
                placeholder={t('contact.message')}
              ></textarea>
            </div>
          </div>

          {submitMessage && (
            <div className={`mt-4 p-3 rounded-lg text-center ${
              submitMessage.includes('successfully') 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {submitMessage}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-kioo-primary text-white px-4 py-2 rounded-lg hover:bg-kioo-secondary transition-colors disabled:opacity-50"
            >
              {isSubmitting ? t('forms.processing') : t('contact.sendMessage')}
            </button>
          </div>
        </form>

        <div className="bg-gray-50 p-4 rounded-b-xl">
          <div className="text-center text-sm text-gray-600 space-y-1">
            <p>🇱🇷 <a href="tel:+231777981981" className="text-kioo-primary">+231 777 981 981</a> · <a href="tel:+231887981981" className="text-kioo-primary">+231 887 981 981</a></p>
            <p>🇸🇱 <a href="tel:+23290061389" className="text-kioo-primary">+232 90 061 389</a> · 🇬🇳 <a href="tel:+224626718421" className="text-kioo-primary">+224 626 718 421</a></p>
            <p>📧 <a href="mailto:info@kiooradio.org" className="text-kioo-primary">info@kiooradio.org</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;